import { z } from 'zod';

/**
 * Sketch Schemas for Shader Node
 * Defines the Sketch entity and related enums for shader/creative coding nodes
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Sketch categories for organization and filtering
 */
export const SketchCategorySchema = z.enum([
  'effects',
  'generators',
  'visualizers',
  'interactive',
  'audio-reactive',
  '3d',
  'particles',
  'fractals',
  'typography',
  'utilities',
]);

/**
 * Runtime mode determines the rendering engine
 */
export const RuntimeModeSchema = z.enum([
  'p5',
  'glsl',
  'threejs',
  'raw',
]);

/**
 * Shader node active tab for UI state
 */
export const ShaderActiveTabSchema = z.enum([
  'library',
  'prompt',
  'code',
  'preview',
  'export',
]);

// ============================================================================
// EXTRACTED PARAM SCHEMA
// ============================================================================

/**
 * Parameter type for extracted shader parameters
 */
export const ExtractedParamTypeSchema = z.enum([
  'float',
  'int',
  'bool',
  'color',
  'select',
]);

/**
 * Extracted parameter from shader code
 * Used for auto-generated UI controls
 */
export const ExtractedParamSchema = z.object({
  name: z.string(),
  type: ExtractedParamTypeSchema,
  min: z.number().optional(),
  max: z.number().optional(),
  default: z.union([z.number(), z.boolean(), z.string()]),
  options: z.array(z.string()).optional(),
  label: z.string().optional(),
});

// ============================================================================
// SKETCH ENTITY SCHEMA
// ============================================================================

/**
 * Sketch entity — a saved creative coding sketch
 * Can be user-created, system library, or forked from another sketch
 */
export const SketchSchema = z.object({
  id: z.string(),
  userId: z.string(),
  canvasId: z.string().optional(),
  nodeId: z.string().optional(),
  code: z.string(),
  runtimeMode: RuntimeModeSchema,
  name: z.string(),
  description: z.string().optional(),
  tags: z.array(z.string()).default([]),
  category: SketchCategorySchema,
  isLibraryItem: z.boolean().default(false),
  isSystemLibrary: z.boolean().default(false),
  forkedFrom: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  previewVideoUrl: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type SketchCategory = z.infer<typeof SketchCategorySchema>;
export type RuntimeMode = z.infer<typeof RuntimeModeSchema>;
export type ShaderActiveTab = z.infer<typeof ShaderActiveTabSchema>;
export type ExtractedParamType = z.infer<typeof ExtractedParamTypeSchema>;
export type ExtractedParam = z.infer<typeof ExtractedParamSchema>;
export type Sketch = z.infer<typeof SketchSchema>;
